import React, { Fragment, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from "react-i18next";

import { Container, Paper, Stepper, Step, StepLabel, Typography, Box, useMediaQuery, Grid, CssBaseline } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import IdentifyUser from './cmponents/IdentifyUser';
import ResetPassword from './cmponents/ResetPassword';
import PasswordChanged from './cmponents/PasswordChanged';

import PageNames from '../common/utils/PageNames';

import { usePageLayoutConfigurator } from "../common/hooks/usePageLayoutConfigurator";
import Logo from '../common/ui/qvshop/components/Logo';
import ReactRouterLink from '../common/ui/qvshop/components/core/ReactRouterLink';

import { resetState, selectActiveStep, setActiveStep } from '../common/store/passwordForgottenSlice';

export default function PasswordForgotten() {
    usePageLayoutConfigurator(PageNames.PASSWORD_FORGOTTEN);
    const { t } = useTranslation(['common', 'passwordForgotten']);
    const dispatch = useDispatch();
    const theme = useTheme();
    const mobile = useMediaQuery(theme.breakpoints.down('sm'));
    const activeStep = useSelector(selectActiveStep);
    const steps = ['passwordForgotten:step.IdentifyUser', 'passwordForgotten:step.ResetPassword', 'passwordForgotten:step.PasswordChanged'];
    const visibleStep = activeStep >= 0 && activeStep < steps.length ? activeStep : 0;

    useEffect(() => {
        return () => {
            dispatch(resetState())
        }
    }, [dispatch]);

    const getStepContent = (step) => {
        switch (step) {
            case 0:
                return <IdentifyUser />;
            case 1:
                return <ResetPassword />;
            case 2:
                return <PasswordChanged  moveForward={handleNext} />;
            default:
                return <IdentifyUser />;
        }
    }

    const handleNext = () => {
        setActiveStep(activeStep + 1);
    };

    return (
        <Container
            component="main"
            maxWidth={false}
            disableGutters
            sx={{
                minHeight: '100vh',
                bgcolor: '#F5F5F5',
                display: 'flex',
                justifyContent: 'center',
                px: 2
            }}
        >
            <CssBaseline />
            <Box sx={{ width: '100%', maxWidth: 600 }}>
                <Box
                    sx={{
                        marginTop: 8,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                    }}
                >
                    <Logo position="relative" sx={{ m: 1, maxWidth: "300px", objectFit: "contain" }} />
                    <Paper
                        sx={{
                            mt: '50px',
                            p: { xs: 2, md: 3 },
                            pt: '25px',
                            width: '100%',
                            bgcolor: '#fff',
                            boxShadow: '0 1px 1.5px rgba(0, 0, 0, 0.11)!important',
                            borderRadius: '12px'
                        }}
                    >
                        <Typography component="h1" variant="h4" align="center">
                            {t("passwordForgotten:page.title")}
                        </Typography>
                        <Stepper activeStep={visibleStep} sx={{ pt: 3, pb: 5 }} orientation={mobile ? "vertical" : "horizontal"}>
                            {steps.map((label) => (
                                <Step key={label}>
                                    <StepLabel>{t(label)}</StepLabel>
                                </Step>
                            ))}
                        </Stepper>
                        <Fragment>
                            {getStepContent(visibleStep)}
                            <Box>
                                <Grid container justifyContent="center">
                                    <Grid item>
                                        <ReactRouterLink to="/login">{t("passwordForgotten:button.toLogin")}</ReactRouterLink>
                                    </Grid>
                                </Grid>
                            </Box>
                        </Fragment>
                    </Paper>
                </Box>
            </Box>
        </Container>
    );
}
